import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Chip, Stack, TextField, MenuItem,
  FormControl, InputLabel, Select, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Divider, Tooltip, CircularProgress,
  useTheme, useMediaQuery
} from '@mui/material';
import {
  ListAlt as ActionsIcon, Refresh as RefreshIcon, Public as PublicIcon,
  LockOpen as InternalIcon, ErrorOutline as ErrorIcon, Person as PersonIcon,
  Security as SecurityIcon, Close as CloseIcon, Visibility as VisibilityIcon,
  Fingerprint as OKeyIcon, Timer as TimerIcon
} from '@mui/icons-material';
import { userActionApi, UserActionEntry, UserActionStatistics } from '../services/userActionApi';
import { useLanguage } from '../contexts/LanguageContext';
import { blobticMessage } from '../components/BlobticMessage';
import GenericPage from '../components/GenericPage';
import GenericTable from '../components/GenericTable';

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

function statusColor(status: number): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status >= 500) return 'error';
  if (status === 429 || status === 401 || status === 403) return 'warning';
  if (status >= 400) return 'warning';
  return 'success';
}

function methodColor(method: string): 'info' | 'success' | 'warning' | 'error' | 'secondary' | 'default' {
  switch (method.toUpperCase()) {
    case 'GET': return 'info';
    case 'POST': return 'success';
    case 'PUT': case 'PATCH': return 'warning';
    case 'DELETE': return 'error';
    default: return 'default';
  }
}

function formatDuration(ms: number): string {
  if (!ms && ms !== 0) return '-';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
  return `${ms} ms`;
}

function formatDate(value: string): string {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-DZ');
  } catch {
    return value;
  }
}

const UserActionCard: React.FC<{ entry: UserActionEntry; onDetail: () => void; t: (k: string, ...a: any[]) => string }> = ({ entry, onDetail, t }) => {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, mb: 1.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip label={entry.UAD_METHOD} size="small" color={methodColor(entry.UAD_METHOD)} sx={{ fontSize: '0.7rem', height: 20 }} />
            <Chip label={String(entry.UAD_STATUS)} size="small" color={statusColor(entry.UAD_STATUS)} sx={{ fontSize: '0.7rem', height: 20 }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">{formatDate(entry.UAD_START)}</Typography>
        </Stack>
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{entry.UAD_PATH}{entry.UAD_QUERY ? `?${entry.UAD_QUERY}` : ''}</Typography>
        {entry.UAD_OKEY && (
          <Chip label={entry.UAD_OKEY} size="small" variant="outlined" color="info" sx={{ fontSize: '0.6rem', height: 18, maxWidth: '100%' }} />
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={formatDuration(entry.UAD_DURATION_MS)} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
          {entry.USER_MAIL && <Chip label={entry.USER_MAIL} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
          {entry.CNX_IDF && <Chip label={`CNX #${entry.CNX_IDF}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
          {entry.UAD_IP && <Chip label={entry.UAD_IP} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
          <Chip
            label={entry.UAD_SOURCE === 'public' ? 'public' : 'interne'}
            size="small"
            color={entry.UAD_SOURCE === 'public' ? 'info' : 'default'}
            variant={entry.UAD_SOURCE === 'public' ? 'outlined' : 'filled'}
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Stack>
        <Button size="small" variant="outlined" color="primary" startIcon={<VisibilityIcon fontSize="small" />} onClick={onDetail} fullWidth>
          {t('useraction.details')}
        </Button>
      </Stack>
    </Paper>
  );
};

export const UserActionsPage: React.FC = () => {
  const { t } = useLanguage();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [stats, setStats] = useState<UserActionStatistics | null>(null);
  const [entries, setEntries] = useState<UserActionEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState<number>(24);
  const [source, setSource] = useState<string>('');
  const [method, setMethod] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [okeyFilter, setOkeyFilter] = useState<string>('');
  const [textFilter, setTextFilter] = useState<string>('');
  const [onlyConnected, setOnlyConnected] = useState<boolean>(false);
  const [selected, setSelected] = useState<UserActionEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await userActionApi.select({
        hours,
        limit: 200,
        source: source || undefined,
        method: method || undefined,
        status: statusFilter || undefined,
        okey: okeyFilter || undefined,
        text: textFilter || undefined,
        onlyConnected,
      });
      setStats(result.statistics);
      setEntries(result.data || []);
    } catch (e: any) {
      blobticMessage.error(t('useraction.error_load'), e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [hours, source, method, statusFilter, okeyFilter, textFilter, onlyConnected, blobticMessage.error, t]);

  useEffect(() => {
    load();
  }, [load]);

  const statCards: { label: string; value: number | string; icon: React.ReactNode; color: string }[] = [
    { label: t('useraction.stats.total'), value: stats?.total_requests ?? 0, icon: <ActionsIcon />, color: '#3b82f6' },
    { label: t('useraction.stats.connected'), value: stats?.connected_requests ?? 0, icon: <PersonIcon />, color: '#22c55e' },
    { label: t('useraction.stats.public'), value: stats?.public_requests ?? 0, icon: <PublicIcon />, color: '#8b5cf6' },
    { label: t('useraction.stats.internal'), value: stats?.internal_requests ?? 0, icon: <InternalIcon />, color: '#06b6d4' },
    { label: t('useraction.stats.distinct_users'), value: stats?.distinct_users ?? 0, icon: <PersonIcon />, color: '#f59e0b' },
    { label: t('useraction.stats.distinct_okeys'), value: stats?.distinct_okeys ?? 0, icon: <OKeyIcon />, color: '#64748b' },
    { label: t('useraction.stats.avg_duration'), value: stats ? formatDuration(stats.avg_duration_ms) : '-', icon: <TimerIcon />, color: '#f97316' },
    { label: t('useraction.stats.server_errors'), value: stats?.server_errors ?? 0, icon: <ErrorIcon />, color: '#dc2626' },
  ];

  return (
    <GenericPage
      title={t('useraction.title')}
      icon={<ActionsIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} sx={{ borderRadius: 2 }}>
            {t('useraction.refresh')}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {statCards.map(card => (
          <Grid item xs={6} sm={3} lg={1.5} key={card.label}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5, borderRadius: 3, height: '100%', textAlign: 'center',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc'
              }}
            >
              <Box sx={{ color: card.color, display: 'flex', justifyContent: 'center', mb: 0.5 }}>{card.icon}</Box>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1 }}>{card.value}</Typography>
              <Typography variant="caption" color="text.secondary">{card.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label={t('useraction.filter.period')}
              select
              size="small"
              fullWidth
              sx={fsx}
              value={hours}
              onChange={e => setHours(Number(e.target.value))}
            >
              <MenuItem value={1}>{t('useraction.filter.last_hour')}</MenuItem>
              <MenuItem value={24}>{t('useraction.filter.last_24h')}</MenuItem>
              <MenuItem value={168}>{t('useraction.filter.last_7d')}</MenuItem>
              <MenuItem value={720}>{t('useraction.filter.last_30d')}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={fsx}>
              <InputLabel>{t('useraction.filter.source')}</InputLabel>
              <Select value={source} label={t('useraction.filter.source')} onChange={e => setSource(e.target.value)}>
                <MenuItem value="">{t('useraction.filter.all')}</MenuItem>
                <MenuItem value="internal">{t('useraction.stats.internal')}</MenuItem>
                <MenuItem value="public">{t('useraction.stats.public')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={fsx}>
              <InputLabel>{t('useraction.filter.method')}</InputLabel>
              <Select value={method} label={t('useraction.filter.method')} onChange={e => setMethod(e.target.value)}>
                <MenuItem value="">{t('useraction.filter.all')}</MenuItem>
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={fsx}>
              <InputLabel>{t('useraction.filter.status')}</InputLabel>
              <Select value={statusFilter} label={t('useraction.filter.status')} onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="">{t('useraction.filter.all')}</MenuItem>
                <MenuItem value="200">200</MenuItem>
                <MenuItem value="400">400</MenuItem>
                <MenuItem value="401">401</MenuItem>
                <MenuItem value="403">403</MenuItem>
                <MenuItem value="404">404</MenuItem>
                <MenuItem value="429">429</MenuItem>
                <MenuItem value="500">500</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label={t('useraction.filter.okey')}
              size="small"
              fullWidth
              sx={fsx}
              value={okeyFilter}
              onChange={e => setOkeyFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label={t('useraction.search_placeholder')}
              size="small"
              fullWidth
              sx={fsx}
              value={textFilter}
              onChange={e => setTextFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                variant={onlyConnected ? 'contained' : 'outlined'}
                color={onlyConnected ? 'primary' : 'inherit'}
                startIcon={<PersonIcon />}
                onClick={() => setOnlyConnected(v => !v)}
                sx={{ borderRadius: 2 }}
              >
                {t('useraction.filter.connected_only')}
              </Button>
              <Chip
                label={t('useraction.filter.count', entries.length)}
                color="primary" variant="outlined"
              />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      ) : isMobile ? (
        <Box>
          {entries.map(entry => (
            <UserActionCard
              key={entry.UAD_IDF}
              entry={entry}
              t={t}
              onDetail={() => setSelected(entry)}
            />
          ))}
        </Box>
      ) : (
        <GenericTable
          columns={[
            { id: 'UAD_START', label: t('useraction.table.date'), minWidth: 150, format: (v: any) => formatDate(v) },
            {
              id: 'UAD_METHOD', label: t('useraction.table.method'), minWidth: 90,
              format: (v: any) => <Chip label={v} size="small" color={methodColor(v)} variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
            },
            {
              id: 'UAD_PATH', label: t('useraction.table.path'), minWidth: 250,
              format: (v: any, row?: any) => (
                <Box>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{v}</Typography>
                  {row?.UAD_QUERY && <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>?{row.UAD_QUERY}</Typography>}
                  {row?.UAD_OKEY && (
                    <Chip
                      label={row.UAD_OKEY} size="small" variant="outlined" color="info"
                      sx={{ fontSize: '0.6rem', height: 18, mt: 0.5, maxWidth: 380 }}
                    />
                  )}
                </Box>
              )
            },
            {
              id: 'UAD_STATUS', label: t('useraction.table.status'), minWidth: 80,
              format: (v: any) => <Chip label={v} size="small" color={statusColor(v)} sx={{ fontSize: '0.7rem', height: 20 }} />
            },
            {
              id: 'UAD_DURATION_MS', label: t('useraction.table.duration'), minWidth: 95,
              format: (v: any) => {
                const slow = v >= 5000;
                return <Typography variant="body2" color={slow ? 'error' : 'inherit'} fontWeight={slow ? 700 : 400}>{formatDuration(v)}</Typography>;
              }
            },
            {
              id: 'USER_MAIL', label: t('useraction.table.user'), minWidth: 160,
              format: (v: any, row?: any) => (
                <Box>
                  <Typography variant="body2">{v || '-'}</Typography>
                  {row?.CNX_IDF && <Typography variant="caption" color="text.secondary">CNX #{row.CNX_IDF}</Typography>}
                </Box>
              )
            },
            {
              id: 'UAD_SOURCE', label: t('useraction.table.source'), minWidth: 90,
              format: (v: any) => v === 'public'
                ? <Chip label="public" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                : <Chip label="interne" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
            },
            { id: 'UAD_IP', label: t('useraction.table.ip'), minWidth: 110, format: (v: any) => v || '-' },
          ]}
          data={entries}
          searchPlaceholder={t('useraction.search_placeholder')}
          rowActions={(row) => (
            <Tooltip title={t('useraction.details')}>
              <IconButton size="small" onClick={() => setSelected(row)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          maxHeight="calc(100vh - 480px)"
        />
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {t('useraction.details_title')}
            {selected?.CNX_IDF && <Chip label={`CNX #${selected.CNX_IDF}`} size="small" color="primary" variant="outlined" />}
          </Stack>
          <IconButton size="small" onClick={() => setSelected(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`#${selected.UAD_IDF}`} size="small" variant="outlined" />
                <Chip label={selected.UAD_METHOD} size="small" color={methodColor(selected.UAD_METHOD)} />
                <Chip label={String(selected.UAD_STATUS)} size="small" color={statusColor(selected.UAD_STATUS)} />
                <Chip label={formatDuration(selected.UAD_DURATION_MS)} size="small" variant="outlined" />
                <Chip label={selected.UAD_IP || '-'} size="small" variant="outlined" />
                {selected.USER_MAIL && <Chip label={selected.USER_MAIL} size="small" color="primary" variant="outlined" />}
                {selected.UAD_SOURCE === 'public'
                  ? <Chip label="public" size="small" color="info" variant="outlined" />
                  : <Chip label="interne" size="small" />}
              </Stack>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <b>{selected.UAD_METHOD}</b> {selected.UAD_PATH}{selected.UAD_QUERY ? `?${selected.UAD_QUERY}` : ''}
              </Typography>
              {selected.UAD_OKEY && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <OKeyIcon fontSize="small" color="info" />
                  <Chip label={selected.UAD_OKEY} size="small" variant="outlined" color="info" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }} />
                </Stack>
              )}
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('useraction.times.start')}</Typography>
                  <Typography variant="body2">{formatDate(selected.UAD_START)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('useraction.times.end')}</Typography>
                  <Typography variant="body2">{formatDate(selected.UAD_END || '')}</Typography>
                </Box>
              </Stack>
              {selected.UAD_BODY && (
                <Box>
                  <Typography variant="subtitle2">{t('useraction.request_body')}</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default', maxHeight: 220, overflow: 'auto' }}>
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                      {selected.UAD_BODY}
                    </Typography>
                  </Paper>
                </Box>
              )}
              {selected.UAD_ERROR && (
                <Box>
                  <Typography variant="subtitle2" color="error">{t('useraction.table.error')}</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                      {selected.UAD_ERROR}
                    </Typography>
                  </Paper>
                </Box>
              )}
              <Divider />
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">{t('useraction.table.user_agent')}</Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{selected.UAD_USER_AGENT || '-'}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">{t('useraction.correlation_id')}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{selected.UAD_CORRELATION_ID || '-'}</Typography>
              </Stack>
              {selected.UAD_TOKEN_HASH && (
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">{t('useraction.table.token_hash')}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{selected.UAD_TOKEN_HASH}</Typography>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} color="inherit">{t('useraction.close')}</Button>
        </DialogActions>
      </Dialog>
    </GenericPage>
  );
};

export default UserActionsPage;
